import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, SlidersHorizontal } from "lucide-react";
import { Logo } from "../components/Logo";
import { CognitiveSearch } from "../components/CognitiveSearch";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Footer } from "../components/Footer";
import { CognitiveLinkCard } from "../components/CognitiveLinkCard";
import { CognitiveLoader } from "../components/CognitiveLoader";
import { MiniGraph } from "../components/MiniGraph";
import { cognitiveSearch, normalizeGraph } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import {
  COGNITIVE_LINKS,
  COGNITIVE_SUMMARY,
  RECOGNIZED_INTENT,
  CONCEPT_CLUSTERS,
} from "../data/mock";

const MOCK_RESULT = {
  recognized_intent: RECOGNIZED_INTENT,
  cognitive_summary: COGNITIVE_SUMMARY,
  links: COGNITIVE_LINKS,
  concept_clusters: CONCEPT_CLUSTERS,
};

const FILTERS = [
  { key: "reasoning", labelKey: "results.reasoningMode", options: ["Fast", "Balanced", "Deep"] },
  { key: "attitude", labelKey: "results.attitude", options: ["Neutral", "Creative", "Critical"] },
  { key: "depth", labelKey: "results.depthLevel", options: ["Overview", "Standard", "Exhaustive"] },
];

const NAV_GRAPH = {
  nodes: [
    { id: "a", label: "Attention", x: 40, y: 30, active: true },
    { id: "b", label: "Embeddings", x: 150, y: 60 },
    { id: "c", label: "Scaling", x: 240, y: 30, active: true },
    { id: "d", label: "Context", x: 150, y: 100 },
  ],
  edges: [
    ["a", "b"],
    ["b", "c"],
    ["b", "d"],
    ["a", "d"],
  ],
};

const FilterGroup = ({ filter, value, onChange, t }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-widest text-[#A7B4C2]/60 mb-2">
      {t(filter.labelKey)}
    </p>
    <div className="flex flex-col gap-1.5">
      {filter.options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            data-testid={`filter-${filter.key}-${opt.toLowerCase()}`}
            onClick={() => onChange(filter.key, opt)}
            className={`flex items-center justify-between rounded-lg border px-3 py-2 font-mono text-[12px] transition-colors duration-300 ${
              active
                ? "border-[#4BE3FF]/50 bg-[#4BE3FF]/10 text-[#4BE3FF]"
                : "border-white/10 text-[#A7B4C2] hover:border-[#1E6FFF]/50"
            }`}
          >
            {t(`results.opt.${opt}`)}
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                active ? "bg-[#4BE3FF] cyan-glow" : "bg-white/15"
              }`}
            />
          </button>
        );
      })}
    </div>
  </div>
);

export default function Results() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || RECOGNIZED_INTENT.query;
  const [filters, setFilters] = useState({
    reasoning: searchParams.get("reasoning") || "Balanced",
    attitude: searchParams.get("attitude") || "Creative",
    depth: "Standard",
  });
  const onChange = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    cognitiveSearch({
      query,
      reasoning: searchParams.get("reasoning") || "Balanced",
      attitude: searchParams.get("attitude") || "Creative",
      language: searchParams.get("language") || "Auto",
    })
      .then((data) => {
        if (cancelled) return;
        if (!data.links || data.links.length === 0) throw new Error("empty");
        setResult(data);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error(t("results.toastError"));
        setResult(MOCK_RESULT);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, searchParams]);

  return (
    <div className="min-h-screen bg-[#0A1A2F]">
      {/* Top bar */}
      <div className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-6">
          <Logo showText={false} />
          <Link
            to="/"
            data-testid="results-back"
            className="flex items-center gap-1.5 font-mono text-[13px] text-[#A7B4C2] hover:text-[#4BE3FF] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t("results.home")}
          </Link>
          <div className="flex-1 max-w-2xl">
            <CognitiveSearch compact initialQuery={query} />
          </div>
          <LanguageSwitcher className="hidden md:flex" />
        </div>
      </div>

      {loading || !result ? (
        <CognitiveLoader query={query} />
      ) : (
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <p className="font-mono text-[12px] uppercase tracking-widest text-[#4BE3FF]">
            {t("results.resultsFor")}
          </p>
          <h1 className="font-display font-semibold text-2xl md:text-3xl text-white mt-2 max-w-3xl">
            {query}
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left column — Intent & Summary */}
          <aside className="lg:col-span-3 lg:sticky lg:top-28 space-y-5">
            <div
              data-testid="recognized-intent-card"
              className="rounded-2xl border border-[#A7B4C2]/15 bg-[#1E6FFF]/[0.07] p-6"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A7B4C2]/60">
                {t("results.recognizedIntent")}
              </p>
              <p className="font-display text-xl text-white mt-2">
                {result.recognized_intent.label}
              </p>
              <div className="mt-4 w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-[#1E6FFF] to-[#4BE3FF]"
                  initial={{ width: 0 }}
                  animate={{ width: `${result.recognized_intent.confidence * 100}%` }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                />
              </div>
              <p className="font-mono text-[11px] text-[#4BE3FF] mt-2">
                {(result.recognized_intent.confidence * 100).toFixed(0)}% {t("results.confidence")}
              </p>
              {result.recognized_intent.entities?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {result.recognized_intent.entities.map((e) => (
                    <span
                      key={e}
                      className="font-mono text-[10px] text-white/80 border border-white/15 rounded-full px-2 py-0.5"
                    >
                      {e}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div
              data-testid="cognitive-summary-card"
              className="rounded-2xl border border-[#A7B4C2]/15 bg-[#1E6FFF]/[0.07] p-6"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A7B4C2]/60 mb-3">
                {t("results.cognitiveSummary")}
              </p>
              <p className="font-ui text-[14px] text-[#F5F7FA]/90 leading-relaxed">
                {result.cognitive_summary}
              </p>
            </div>
          </aside>

          {/* Center column — Cognitive links */}
          <main className="lg:col-span-6 space-y-6">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[12px] text-[#A7B4C2]">
                {result.links.length} {t("results.linksRanked")}
              </p>
              <span className="font-mono text-[11px] text-[#4BE3FF] flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#4BE3FF] animate-pulse" />
                {t("results.live")}
              </span>
            </div>
            {result.links.map((link, i) => (
              <CognitiveLinkCard key={link.id} link={link} index={i} />
            ))}
          </main>

          {/* Right column — Cognitive filters */}
          <aside className="lg:col-span-3 lg:sticky lg:top-28 space-y-6">
            <div className="rounded-2xl border border-[#A7B4C2]/15 bg-[#05101f]/70 p-6 space-y-6">
              <div className="flex items-center gap-2 text-white">
                <SlidersHorizontal className="w-4 h-4 text-[#4BE3FF]" />
                <p className="font-display font-semibold text-base">
                  {t("results.cognitiveFilters")}
                </p>
              </div>
              {FILTERS.map((f) => (
                <FilterGroup
                  key={f.key}
                  filter={f}
                  value={filters[f.key]}
                  onChange={onChange}
                  t={t}
                />
              ))}
            </div>

            <div className="rounded-2xl border border-[#A7B4C2]/15 bg-[#05101f]/70 p-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A7B4C2]/60 mb-3">
                {t("results.conceptClusters")}
              </p>
              <div className="space-y-2">
                {result.concept_clusters.map((c) => (
                  <button
                    key={c.label}
                    data-testid={`cluster-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="w-full flex items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-[#4BE3FF]/5 transition-colors group"
                  >
                    <span className="font-mono text-[12px] text-[#A7B4C2] group-hover:text-[#4BE3FF] transition-colors">
                      {c.label}
                    </span>
                    <span className="font-mono text-[11px] text-[#4BE3FF]/70">
                      {c.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-[#A7B4C2]/15 bg-[#05101f]/70 p-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A7B4C2]/60 mb-3">
                {t("results.graphNavigation")}
              </p>
              <MiniGraph
                nodes={NAV_GRAPH.nodes}
                edges={NAV_GRAPH.edges}
                width={280}
                height={130}
              />
            </div>
          </aside>
        </div>
      </div>
      )}
      <Footer />
    </div>
  );
}
